/**
 * Which notification switches each user sees on the web Notification
 * settings page, and exactly what each one controls today. The mobile app
 * has the same rows (mobile/src/utils/notificationPreferenceRows.ts); keep
 * them in step (both test suites pin the same labels and keys).
 *
 * Only switches some sender actually reads are shown (checked 2026-10-14):
 *   - email_enabled      notifications API: message emails (_db_messages) and
 *                        the office "Send Notification" email (/send).
 *   - message_received   notifications API: message emails only.
 *   - push_enabled       commons/push_service: every app alert. Family alerts
 *                        go to ONE guardian per family (send_to_family), so
 *                        only that parent sees the alert switches; the API
 *                        says who that is (receives_app_alerts).
 *   - statement_available  push for a new invoice (invoice_generator).
 *   - autopay_failure    push from batch-jobs/autopay.
 *   - card_expiry_warning  push from batch-jobs/housekeeping.
 *   - reminders_enabled  push for overdue invoices (batch-jobs/dunning).
 *   - hifdh_daily_update the evening Hifdh update (hifdh daily_update.py).
 *                        Unlike the family alerts it goes to EVERY guardian
 *                        of a child with a Hifdh plan, so those parents get
 *                        "App alerts" too; the API says who (receives_hifdh_updates).
 *   - hifdh_certificate  a Hifdh certificate awarded (hifdh API, on award).
 *                        Also to every guardian of a child with a Hifdh plan;
 *                        the API says who (receives_hifdh_certificates).
 * Not shown, because nothing sends them yet: sms_enabled (no SMS sender),
 * payment_due_reminder (no "payment due" sender), roster_change (no sender).
 *
 * Billing emails (invoices, receipts, statements, overdue and AutoPay
 * notices) don't read these settings: they always go to the family's
 * primary guardian. The page says so.
 */

import type { NotificationPreferenceToggle } from "@/types/notification";

export interface NotificationSettingRow {
  key: NotificationPreferenceToggle;
  label: string;
  description: string;
}

/** Section headings, the same on web and mobile. */
export const CHANNELS_HEADING = "How to reach you";
export const EVENTS_HEADING = "What to notify you about";

const EMAIL: NotificationSettingRow = {
  key: "email_enabled",
  label: "Email from IlmIQ",
  description:
    "Emails about new messages, and notices the school sends you from IlmIQ",
};

const PUSH: NotificationSettingRow = {
  key: "push_enabled",
  label: "App alerts",
  description: "Alerts on your phone from the IlmIQ app",
};

const MESSAGES: NotificationSettingRow = {
  key: "message_received",
  label: "New messages",
  description: "Email me when someone sends me a message in IlmIQ",
};

const ALERT_ROWS: NotificationSettingRow[] = [
  {
    key: "statement_available",
    label: "New invoices",
    description: "App alert when the school issues a new invoice",
  },
  {
    key: "reminders_enabled",
    label: "Overdue invoices",
    description: "App alert when an invoice becomes overdue",
  },
  {
    key: "autopay_failure",
    label: "AutoPay failed",
    description: "App alert when an AutoPay charge doesn't go through",
  },
  {
    key: "card_expiry_warning",
    label: "Card expiring",
    description: "App alert before a saved card expires",
  },
];

const HIFDH: NotificationSettingRow = {
  key: "hifdh_daily_update",
  label: "Hifdh daily update",
  description: "App alert in the evening: how Hifdh went and what to practice at home",
};

const HIFDH_CERTIFICATE: NotificationSettingRow = {
  key: "hifdh_certificate",
  label: "Hifdh certificates",
  description: "App alert when the school awards your child a juz or khatm certificate",
};

const ALERT_KEYS = new Set<NotificationPreferenceToggle>([
  ...ALERT_ROWS.map((r) => r.key),
  HIFDH.key,
  HIFDH_CERTIFICATE.key,
]);

/** Single app-alert rows say "this"; the family alert group says "these". */
const SINGLE_ALERTS = new Set<NotificationPreferenceToggle>([HIFDH.key, HIFDH_CERTIFICATE.key]);

/** Does this user get the family app alerts? (Only that parent sees them.) */
const getsAlerts = (role: string | null | undefined, receivesAppAlerts?: boolean) =>
  role === "parent" && receivesAppAlerts === true;

/** Does this user get the evening Hifdh update? (Every parent of a Hifdh child.) */
const getsHifdh = (role: string | null | undefined, receivesHifdhUpdates?: boolean) =>
  role === "parent" && receivesHifdhUpdates === true;

/** How to reach you: the delivery channels that do something for this user. */
export function channelRowsForRole(
  role: string | null | undefined,
  receivesAppAlerts?: boolean,
  receivesHifdhUpdates?: boolean,
  receivesHifdhCertificates?: boolean,
): NotificationSettingRow[] {
  return getsAlerts(role, receivesAppAlerts) ||
    getsHifdh(role, receivesHifdhUpdates) ||
    getsHifdh(role, receivesHifdhCertificates)
    ? [EMAIL, PUSH]
    : [EMAIL];
}

/** What to notify me about: the event switches that do something for this user. */
export function eventRowsForRole(
  role: string | null | undefined,
  receivesAppAlerts?: boolean,
  receivesHifdhUpdates?: boolean,
  receivesHifdhCertificates?: boolean,
): NotificationSettingRow[] {
  return [
    MESSAGES,
    ...(getsHifdh(role, receivesHifdhUpdates) ? [HIFDH] : []),
    ...(getsHifdh(role, receivesHifdhCertificates) ? [HIFDH_CERTIFICATE] : []),
    ...(getsAlerts(role, receivesAppAlerts) ? ALERT_ROWS : []),
  ];
}

/**
 * A row that can't do anything with the current settings is shown disabled
 * with a caption saying why: message emails need "Email from IlmIQ" on
 * (notifications/_db_messages), and every app alert needs "App alerts" on
 * (push_service._check_preferences).
 */
export function rowState(
  row: NotificationSettingRow,
  prefs: Partial<Record<NotificationPreferenceToggle, boolean>>,
): { disabled: boolean; description: string } {
  if (row.key === "message_received" && prefs.email_enabled === false) {
    return {
      disabled: true,
      description: "Turn on Email from IlmIQ to get message emails",
    };
  }
  if (ALERT_KEYS.has(row.key) && prefs.push_enabled === false) {
    return {
      disabled: true,
      description:
        SINGLE_ALERTS.has(row.key)
          ? "Turn on App alerts to get this"
          : "Turn on App alerts to get these",
    };
  }
  return { disabled: false, description: row.description };
}

/** Shown to the parent family app alerts are sent to. */
export const PARENT_ALERTS_RECIPIENT_NOTE =
  "Your family's app alerts about invoices, payments, AutoPay and cards come to you.";

/** Shown to every other parent of the family, instead of the alert switches. */
export const PARENT_ALERTS_ELSEWHERE_NOTE =
  "App alerts about invoices, payments, AutoPay and cards go to your family's primary guardian's phone.";

export const PARENT_BILLING_EMAIL_NOTE =
  "Billing emails are always sent: invoices, payment receipts, statements, " +
  "overdue reminders and AutoPay notices go to your family's primary " +
  "guardian whatever you choose here.";

/** Roles the notifications API lets read and save their own preferences. */
export const NOTIFICATION_SETTINGS_ROLES = [
  "admin",
  "principal",
  "staff",
  "teacher",
  "parent",
] as const;
